import { useMemo } from "react";
import { eventMatchesColumn, isStandaloneCalendarEvent } from "@/lib/calendars";

/**
 * Splits a day's events across the rendered day-view columns.
 *
 * `dayViewColumns` comes from buildDayViewColumns (src/lib/calendars.js): entity
 * (doctor/clinic) columns first, then one column per selected standalone calendar,
 * or an explicit "_all" pool when the entity facet has no loaded options. A
 * calendar column takes exactly its calendar's events; entity columns and the
 * "_all" column never take standalone-calendar events (eventMatchesColumn).
 * `standaloneIdSet` (ids of the active standalone calendars) lets the matcher
 * recognise a standalone event even when it isn't flagged as a direct entry.
 *
 * Returns a fresh object each render — memo off `.columnsToRender`.
 */
export function useDayViewColumns(events, dayViewColumns, groupBy = "tenant", standaloneIdSet) {
  // A single explicitly-selected clinic/doctor still renders as a titled
  // column, so the header keeps saying whose day is on screen.
  const splitColumns = Array.isArray(dayViewColumns) && dayViewColumns.length > 0;

  const columnsToRender = useMemo(() => {
    if (!splitColumns) {
      // The untitled pool column never shows standalone-calendar events (they only
      // ever render in their own calendar's column). Normally the page has already
      // filtered them out when no calendar is selected; this keeps the rule local.
      const poolEvents = events.some((event) => isStandaloneCalendarEvent(event, standaloneIdSet))
        ? events.filter((event) => !isStandaloneCalendarEvent(event, standaloneIdSet))
        : events;
      return [{ id: "_all", title: null, kind: "all", calendarId: "", colorHex: "", events: poolEvents }];
    }
    return dayViewColumns.map((column) => ({
      id: column.id,
      title: column.name,
      kind: column.kind || (column.id === "_all" ? "all" : "entity"),
      calendarId: column.calendarId || "",
      colorHex: column.colorHex || "",
      events: events.filter((event) => eventMatchesColumn(event, column, groupBy, standaloneIdSet)),
    }));
  }, [splitColumns, dayViewColumns, events, groupBy, standaloneIdSet]);

  const sortedEvents = useMemo(
    () =>
      [...columnsToRender.flatMap((c) => c.events)].sort(
        (left, right) => left.startAt.toMillis() - right.startAt.toMillis(),
      ),
    [columnsToRender],
  );

  return { splitColumns, columnsToRender, sortedEvents };
}
